
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Clock, User, Star, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import ArticleEditor from "@/components/admin/ArticleEditor";
import { supabase } from "@/integrations/supabase/client";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";

interface Article {
  id: string;
  title: string;
  content: string;
  author: string | null;
  reading_time_minutes: number | null;
  created_at: string;
  topic_name: string;
  topic_id: number;
  is_published: boolean;
  updated_at: string;
}

interface Feedback {
  question_1_rating: number;
  question_2_rating: number;
  question_1_text: string;
  question_2_text: string;
  additional_comments: string;
}

const KinkipediaArticle = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const { toast } = useToast();
  const { isRTL } = useLanguage();
  
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState<Feedback | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  const [question1Rating, setQuestion1Rating] = useState([5]);
  const [question2Rating, setQuestion2Rating] = useState([5]);
  const [additionalComments, setAdditionalComments] = useState("");

  useEffect(() => {
    if (!adminLoading) {
      fetchArticle();
      if (user) {
        checkExistingFeedback();
      }
    }
  }, [articleId, user, isAdmin, adminLoading]);

  const fetchArticle = async () => {
    if (!articleId) return;

    try {
      const { data, error } = await supabase
        .from('kinkipedia_articles')
        .select('*')
        .eq('id', articleId)
        .single();

      if (error) throw error;
      
      // Check if article is published or if user is admin
      if (!data.is_published && !isAdmin) {
        navigate('/kinkipedia');
        return;
      }
      
      setArticle(data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingFeedback = async () => {
    if (!articleId || !user) return;

    try {
      const { data, error } = await supabase
        .from('kinkipedia_feedback')
        .select('*')
        .eq('article_id', articleId)
        .eq('user_id', user.id)
        .single();

      if (data) {
        setExistingFeedback(data);
        setQuestion1Rating([data.question_1_rating]);
        setQuestion2Rating([data.question_2_rating]);
        setAdditionalComments(data.additional_comments || "");
      }
    } catch (error) {
      // No existing feedback found, which is fine
      console.log('No existing feedback found');
    }
  };

  const deleteArticle = async () => {
    if (!articleId || !article) return;

    try {
      const { error } = await supabase
        .from('kinkipedia_articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Article deleted successfully",
      });
      
      navigate(`/kinkipedia/topic/${article.topic_id}`);
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive",
      });
    }
  };

  const togglePublishStatus = async () => {
    if (!articleId || !article) return;

    try {
      const { error } = await supabase
        .from('kinkipedia_articles')
        .update({ is_published: !article.is_published })
        .eq('id', articleId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Article ${!article.is_published ? 'published' : 'unpublished'} successfully`,
      });
      
      setArticle({ ...article, is_published: !article.is_published });
    } catch (error) {
      console.error('Error updating article:', error);
      toast({
        title: "Error",
        description: "Failed to update article status",
        variant: "destructive",
      });
    }
  };

  const handleArticleSaved = () => {
    setShowEditDialog(false);
    fetchArticle();
  };

  const handleBackClick = () => {
    if (article) {
      navigate(`/kinkipedia/topic/${article.topic_id}`);
    } else {
      navigate('/kinkipedia');
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!user || !articleId) {
      toast({
        title: "Authentication required",
        description: "Please log in to submit feedback",
        variant: "destructive"
      });
      return;
    }

    setFeedbackSubmitting(true);

    try {
      const feedbackData = {
        user_id: user.id,
        article_id: articleId,
        question_1_rating: question1Rating[0],
        question_2_rating: question2Rating[0],
        additional_comments: additionalComments.trim() || null
      };

      if (existingFeedback) {
        // Update existing feedback
        const { error } = await supabase
          .from('kinkipedia_feedback')
          .update(feedbackData)
          .eq('article_id', articleId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Insert new feedback
        const { error } = await supabase
          .from('kinkipedia_feedback')
          .insert(feedbackData);

        if (error) throw error;
      }

      toast({
        title: "Feedback submitted",
        description: "Thank you for your feedback!",
      });

      setShowFeedback(false);
      checkExistingFeedback(); // Refresh feedback data
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  if (loading || adminLoading) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="large" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <div className="text-center text-ice-white">
          <h2 className="text-2xl font-bold mb-2">Article not found</h2>
          <Button 
            onClick={() => navigate('/kinkipedia')} 
            variant="outline"
            className="border-ice-white/30 text-ice-white hover:bg-neon-red/20 hover:border-neon-red"
          >
            Back to Kinkipedia
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-carbon flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
      <AdPanel />
      <RightSideMenu />
      
      <div className="flex-1 p-4 pb-20 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackClick}
            className="text-ice-white hover:bg-carbon/50 hover:text-neon-red border border-ice-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {article.topic_name}
          </Button>

          {/* Admin Controls */}
          {isAdmin && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={togglePublishStatus}
                className={`border-ice-white/30 ${
                  article.is_published 
                    ? 'text-orange-400 hover:bg-orange-500/20' 
                    : 'text-toxic-green hover:bg-toxic-green/20'
                }`}
              >
                {article.is_published ? (
                  <>
                    <EyeOff className="w-4 h-4 mr-2" />
                    Unpublish
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Publish
                  </>
                )}
              </Button>

              <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-ice-white/30 text-ice-white hover:bg-neon-red/20"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Article</DialogTitle>
                    <DialogDescription>
                      Modify the article content and settings
                    </DialogDescription>
                  </DialogHeader>
                  <ArticleEditor 
                    article={article} 
                    onSave={handleArticleSaved} 
                  />
                </DialogContent>
              </Dialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Article</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{article.title}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={deleteArticle}
                      className="bg-destructive hover:bg-destructive/80"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {/* Article */}
        <Card className="bg-carbon/80 border-ice-white/20 text-ice-white mb-6">
          <CardHeader>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant="outline" className="bg-carbon/30 text-ice-white border-ice-white/30">
                {article.topic_name}
              </Badge>
              {isAdmin && !article.is_published && (
                <Badge variant="outline" className="bg-orange-500/20 border-orange-500/30 text-orange-400">
                  Draft
                </Badge>
              )}
            </div>
            <CardTitle className="text-2xl md:text-3xl leading-tight mb-4 text-ice-white">
              {article.title}
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-ice-white/60">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{article.reading_time_minutes || 5} min read</span>
              </div>
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{article.author || 'Unknown'}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert max-w-none">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-ice-white/80 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feedback Section */}
        <Card className="bg-carbon/80 border-ice-white/20 text-ice-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-ice-white">
              <Star className="w-5 h-5 text-toxic-green" />
              Your Feedback
            </CardTitle>
            <p className="text-ice-white/60 text-sm">
              {existingFeedback 
                ? "You've already provided feedback for this article. You can update it below."
                : "Help us improve by sharing your thoughts on this article."
              }
            </p>
          </CardHeader>
          <CardContent>
            {!showFeedback ? (
              <Button
                onClick={() => setShowFeedback(true)}
                className="bdsm-button"
              >
                {existingFeedback ? "Update Feedback" : "Provide Feedback"}
              </Button>
            ) : (
              <div className="space-y-6">
                {/* Question 1 */}
                <div>
                  <label className="block text-ice-white mb-3">
                    Was this article helpful to you? (1 = Not at all, 10 = Extremely helpful)
                  </label>
                  <div className="px-3">
                    <Slider
                      value={question1Rating}
                      onValueChange={setQuestion1Rating}
                      max={10}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-ice-white/60 mt-1">
                      <span>1</span>
                      <span className="font-medium text-neon-red">
                        {question1Rating[0]}
                      </span>
                      <span>10</span>
                    </div>
                  </div>
                </div>

                {/* Question 2 */}
                <div>
                  <label className="block text-ice-white mb-3">
                    Would you recommend this article to others? (1 = Definitely not, 10 = Absolutely)
                  </label>
                  <div className="px-3">
                    <Slider
                      value={question2Rating}
                      onValueChange={setQuestion2Rating}
                      max={10}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-sm text-ice-white/60 mt-1">
                      <span>1</span>
                      <span className="font-medium text-neon-red">
                        {question2Rating[0]}
                      </span>
                      <span>10</span>
                    </div>
                  </div>
                </div>

                {/* Additional Comments */}
                <div>
                  <label className="block text-ice-white mb-2">
                    Additional Comments (optional)
                  </label>
                  <Textarea
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                    placeholder="Share any additional thoughts or suggestions..."
                    className="bg-carbon/50 border-ice-white/20 text-ice-white placeholder-ice-white/40 focus:border-neon-red/50"
                    rows={3}
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleFeedbackSubmit}
                    disabled={feedbackSubmitting}
                    className="bg-toxic-green hover:bg-toxic-green/80 text-carbon font-medium"
                  >
                    {feedbackSubmitting 
                      ? "Submitting..." 
                      : existingFeedback 
                        ? "Update Feedback" 
                        : "Submit Feedback"
                    }
                  </Button>
                  <Button
                    onClick={() => setShowFeedback(false)}
                    variant="outline"
                    className="border-ice-white/30 text-ice-white hover:bg-carbon/50 hover:border-neon-red/50"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KinkipediaArticle;
